import type { StrapiJenisPengukuranMini, StrapiSampleInformationRow } from '../../api/strapiCrm';
import {
  airGroupFromSamplingPlan,
  type SamplingPlanAirGroup as SharedSamplingPlanAirGroup,
} from '../../sampling/samplingPlanBidang';
import { jenisPengukuranAirFlagFromGrouping } from '../../sampling/groupingAirSignal';

export type SamplingPlanAirGroup = SharedSamplingPlanAirGroup;
export { airGroupFromSamplingPlan };

/** Tab / section label when `grouping.perencanaan_sampling === 1` (v1 air). */
export const SAMPLING_PLAN_TAB_LABEL_AIR = 'Rencana pengambilan contoh air dan padatan';

/** Tab / section label when perencanaan_sampling is 2 (udara) or 3 (LB3). */
export const SAMPLING_PLAN_TAB_LABEL_NON_AIR =
  'Rencana pengambilan contoh udara ambien, emisi dan gangguan lingkungan';

export function samplingPlanTabLabel(group: SamplingPlanAirGroup): string {
  return group === 'air' ? SAMPLING_PLAN_TAB_LABEL_AIR : SAMPLING_PLAN_TAB_LABEL_NON_AIR;
}

/** MUI Tabs sx: keep long labels on one line (scroll horizontally instead of wrapping). */
export const workOrderScrollableTabsSx = {
  borderBottom: 1,
  borderColor: 'divider',
  mb: 0,
  minHeight: 48,
  '& .MuiTabs-flexContainer': {
    alignItems: 'stretch',
  },
  '& .MuiTab-root': {
    whiteSpace: 'nowrap',
    maxWidth: 'none',
    minHeight: 48,
    minWidth: 'auto',
    py: 1.5,
    px: 2.5,
    lineHeight: 1.35,
    textTransform: 'none',
    alignItems: 'center',
  },
} as const;

/**
 * Resolve air/non-air from jenis pengukuran → grouping.perencanaan_sampling (v1 signal).
 * Returns null when grouping is missing or code is unset (excluded from both tabs until master data is fixed).
 */
export function jenisPengukuranAirFlag(
  jp: StrapiJenisPengukuranMini | null | undefined,
): boolean | null {
  return jenisPengukuranAirFlagFromGrouping(jp?.grouping);
}

export function sampleLineAirFlag(line: Pick<StrapiSampleInformationRow, 'jenis_pengukuran'>): boolean | null {
  return jenisPengukuranAirFlag(line.jenis_pengukuran);
}

export function sampleLineMatchesAirGroup(
  line: Pick<StrapiSampleInformationRow, 'jenis_pengukuran'>,
  group: SamplingPlanAirGroup,
): boolean {
  const flag = sampleLineAirFlag(line);
  if (flag === null) return false;
  return group === 'air' ? flag === true : flag === false;
}

export function filterSamplesByAirGroup<T extends Pick<StrapiSampleInformationRow, 'jenis_pengukuran'>>(
  samples: T[],
  group: SamplingPlanAirGroup,
): T[] {
  return samples.filter((line) => sampleLineMatchesAirGroup(line, group));
}

export function samplesHaveAirGroup(
  samples: Array<Pick<StrapiSampleInformationRow, 'jenis_pengukuran'>>,
  group: SamplingPlanAirGroup,
): boolean {
  return samples.some((line) => sampleLineMatchesAirGroup(line, group));
}

const CARA_SAMPLING_AIR_PREFIX = 'CARA_SAMPLING_AIR';

/** Air: hanya `CARA_SAMPLING_AIR*`. Non-air: semua selain prefix itu. */
export function surveyRowAirDisplay(isAir: boolean | undefined): string {
  if (isAir === true) return 'Ya';
  if (isAir === false) return 'Tidak';
  return '—';
}

export function filterCaraSamplingReferensiByAirGroup<T>(
  rows: T[],
  group: SamplingPlanAirGroup,
): T[] {
  return rows.filter((row) => {
    const kode = String((row as { kode_kategori?: unknown }).kode_kategori ?? '')
      .trim()
      .toUpperCase();
    const isAirCara = kode.startsWith(CARA_SAMPLING_AIR_PREFIX);
    return group === 'air' ? isAirCara : !isAirCara;
  });
}
